import React, { useState, useEffect } from 'react'
import api from '../services/api'

interface Document {
  id: number
  lead: number
  file: string
  file_name: string
  file_size: number | null
  file_url: string | null
  uploaded_by: number
  uploaded_by_name: string
  created_at: string
}

interface LeadDocumentsProps {
  leadId: number
  leadName: string
  onDocumentUploaded?: () => void
}

const LeadDocuments: React.FC<LeadDocumentsProps> = ({ leadId, leadName, onDocumentUploaded }) => {
  const [documents, setDocuments] = useState<Document[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isUploading, setIsUploading] = useState(false)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)

  useEffect(() => {
    loadDocuments()
  }, [leadId])

  const loadDocuments = async () => {
    try {
      setIsLoading(true)
      const res = await api.get(`/leads/documents/?lead=${leadId}`)
      const data = res.data
      setDocuments(Array.isArray(data) ? data : (data.results || []))
    } catch (err: any) {
      console.error('Error loading documents:', err)
    } finally {
      setIsLoading(false)
    }
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setSelectedFile(file)
    }
  }

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedFile) return

    try {
      setIsUploading(true)
      const formData = new FormData()
      formData.append('file', selectedFile)
      formData.append('file_name', selectedFile.name)
      formData.append('lead', leadId.toString())

      await api.post('/leads/documents/', formData)
      setSelectedFile(null)
      loadDocuments()
      if (onDocumentUploaded) {
        onDocumentUploaded()
      }
      alert('Documento uploaded com sucesso!')
    } catch (err: any) {
      console.error('Error uploading document:', err)
      alert('Erro ao fazer upload: ' + (err.response?.data ? JSON.stringify(err.response.data) : err.message || 'Erro desconhecido'))
    } finally {
      setIsUploading(false)
    }
  }

  const handleDeleteDocument = async (docId: number, docName: string) => {
    if (!window.confirm(`Tem certeza que deseja deletar o documento "${docName}"?`)) {
      return
    }

    try {
      await api.delete(`/leads/documents/${docId}/`)
      loadDocuments()
      alert('Documento deletado com sucesso!')
    } catch (err: any) {
      console.error('Error deleting document:', err)
      alert('Erro ao deletar documento: ' + (err.message || 'Erro desconhecido'))
    }
  }

  const handleDownload = async (doc: Document) => {
    if (!doc.file_url) {
      alert('URL do documento não disponível')
      return
    }

    try {
      const token = localStorage.getItem('token')
      const response = await fetch(doc.file_url, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })

      if (response.ok) {
        const blob = await response.blob()
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = doc.file_name
        document.body.appendChild(a)
        a.click()
        window.URL.revokeObjectURL(url)
        document.body.removeChild(a)
      } else {
        alert('Erro ao baixar documento')
      }
    } catch (err: any) {
      console.error('Error downloading document:', err)
      alert('Erro ao baixar documento: ' + (err.message || 'Erro desconhecido'))
    }
  }

  const formatFileSize = (bytes: number | null) => {
    if (!bytes) return '-'
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
  }

  const getFileIcon = (fileName: string) => {
    const ext = fileName.split('.').pop()?.toLowerCase()
    switch (ext) {
      case 'pdf': return '📄'
      case 'doc':
      case 'docx': return '📝'
      case 'xls':
      case 'xlsx': return '📊'
      case 'ppt':
      case 'pptx': return '📈'
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif': return '🖼️'
      case 'zip':
      case 'rar': return '📦'
      default: return '📎'
    }
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
      {/* Header */}
      <div style={{ borderBottom: '1px solid #e0e0e0', paddingBottom: 'var(--space-4)' }}>
        <h3 style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--font-weight-bold)', marginBottom: 'var(--space-2)' }}>
          📎 Documentos e Anexos
        </h3>
        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>
          Gerencie os documentos do lead
        </p>
      </div>

      {/* Upload Form */}
      <form onSubmit={handleUpload} style={{
        padding: 'var(--space-4)',
        backgroundColor: '#f5f5f5',
        borderRadius: 'var(--radius-md)',
        border: '2px dashed #e0e0e0'
      }}>
        <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: '200px' }}>
            <input
              type="file"
              onChange={handleFileSelect}
              style={{
                width: '100%',
                padding: 'var(--space-2)',
                border: '1px solid #e0e0e0',
                borderRadius: 'var(--radius-md)',
                backgroundColor: 'white'
              }}
            />
          </div>
          <button
            type="submit"
            disabled={!selectedFile || isUploading}
            style={{
              padding: 'var(--space-2) var(--space-4)',
              backgroundColor: 'var(--color-primary-600)',
              color: 'white',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              cursor: (!selectedFile || isUploading) ? 'not-allowed' : 'pointer',
              fontSize: 'var(--text-sm)',
              fontWeight: '500',
              opacity: (!selectedFile || isUploading) ? 0.5 : 1
            }}
          >
            {isUploading ? 'Enviando...' : '📤 Upload'}
          </button>
        </div>
        {selectedFile && (
          <div style={{ marginTop: 'var(--space-2)', fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>
            Arquivo selecionado: <strong>{selectedFile.name}</strong> ({formatFileSize(selectedFile.size)})
          </div>
        )}
      </form>

      {/* Documents List */}
      <div>
        {isLoading ? (
          <div style={{ textAlign: 'center', padding: 'var(--space-8)' }}>
            <div style={{ fontSize: '24px', marginBottom: 'var(--space-2)' }}>⏳</div>
            <p style={{ color: 'var(--text-secondary)' }}>Carregando documentos...</p>
          </div>
        ) : documents.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 'var(--space-8)', color: 'var(--text-tertiary)' }}>
            <div style={{ fontSize: '48px', marginBottom: 'var(--space-2)' }}>📎</div>
            <p style={{ fontSize: '16px', marginBottom: 'var(--space-1)' }}>
              Nenhum documento encontrado
            </p>
            <p style={{ fontSize: '14px' }}>
              Use o formulário acima para fazer upload
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
            {documents.map((doc) => (
              <div
                key={doc.id}
                style={{
                  display: 'flex',
                  gap: 'var(--space-3)',
                  padding: 'var(--space-4)',
                  backgroundColor: 'var(--bg-primary)',
                  border: `1px solid var(--border-light)`,
                  borderRadius: 'var(--radius-md)',
                  boxShadow: 'var(--shadow-sm)',
                  alignItems: 'center'
                }}
              >
                <div
                  style={{
                    width: '50px',
                    height: '50px',
                    borderRadius: '8px',
                    backgroundColor: '#f5f5f5',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '24px',
                    flexShrink: 0
                  }}
                >
                  {getFileIcon(doc.file_name)}
                </div>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 'var(--font-weight-semibold)', fontSize: '14px', marginBottom: '2px' }}>
                    {doc.file_name}
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                    {formatFileSize(doc.file_size)} • {formatDate(doc.created_at)}
                  </div>
                  <div style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>
                    Por: {doc.uploaded_by_name || 'Usuário'}
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={() => handleDownload(doc)}
                    style={{
                      padding: '6px 12px',
                      backgroundColor: '#1976d2',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontWeight: '500'
                    }}
                  >
                    ⬇️ Baixar
                  </button>
                  <button
                    onClick={() => handleDeleteDocument(doc.id, doc.file_name)}
                    style={{
                      padding: '6px 12px',
                      backgroundColor: '#f44336',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontWeight: '500'
                    }}
                  >
                    🗑️ Deletar
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default LeadDocuments
